import type { Catalogue } from "./catalogue";

/**
 * Suomi.
 *
 * Two habits this catalogue keeps.
 *
 * NO GENDER, no second-person guessing: Finnish has neither problem, so the
 * plain second person ("Kirjauduit sisään") is used where English uses it.
 *
 * DATES CARRY THE ORDINAL DOT. "9 Sep" is "9. syys" here, and the dot belongs
 * to the language rather than to the formatter - which is why `time.onDate` is
 * a template with the day, month and time as separate values instead of a
 * string the code assembles.
 */
const fi: Catalogue = {
  common: {
    loading: "ladataan…",
    save: "Tallenna",
    saving: "Tallennetaan…",
    cancel: "Peruuta",
    close: "Sulje",
    delete: "Poista",
    edit: "Muokkaa",
    retry: "Yritä uudelleen",
    search: "Haku",
    filter: "Suodata",
    all: "Kaikki",
    none: "ei mitään",
    unknown: "tuntematon",
    yes: "kyllä",
    no: "ei",
    more: "Lisää",
    back: "Takaisin",
    copy: "Kopioi",
    copied: "Kopioitu",
    dash: "—",
    crumbs: "Missä tämä sivu on",
  },

  nav: {
    campaigns: "Kampanjat",
    account: "Tili",
    now: "Nyt",
    projects: "Projektit",
    benches: "Penkit",
    runs: "Ajot",
    reports: "Raportit",
    quality: "Laatu",
    manual: "Manuaalitestit",
    people: "Käyttäjät",
    more: "Lisää",
    menu: "Valikko",
    main: "päävalikko",
    signOut: "Kirjaudu ulos",
    readOnly: "vain luku",
    readOnlyTitle: "vain luku",
    admin: "ylläpitäjä",
    adminTitle: "instanssin ylläpitäjä",
    noPassword: "salasanaa ei asetettu",
    notFound: "Sivua ei ole.",
  },

  theme: {
    label: "Väriteema",
    light: "Vaalea",
    auto: "Järjestelmän mukaan",
    dark: "Tumma",
  },

  lang: {
    uncheckedFi: "Tämä suomennos on tehty ilman suomen puhujaa eikä sitä ole tarkistettu. Jos jokin kuulostaa väärältä, kerro — se korjataan.",
    label: "Kieli",
  },

  time: {
    justNow: "juuri nyt",
    minutesAgo_one: "{{count}} min sitten",
    minutesAgo_other: "{{count}} min sitten",
    hoursAgo_one: "{{count}} h sitten",
    hoursAgo_other: "{{count}} h sitten",
    daysAgo_one: "{{count}} päivä sitten",
    daysAgo_other: "{{count}} päivää sitten",
    days: "su ma ti ke to pe la",
    months: "tammi helmi maalis huhti touko kesä heinä elo syys loka marras joulu",
    onDate: "{{day}}. {{month}}, {{time}}",
    onDateWithYear: "{{day}}. {{month}} {{year}}, {{time}}",
    onDay: "{{day}}. {{month}} {{year}}",
    durationHours: "{{h}} t {{m}} min",
    durationMinutes: "{{m}} min {{s}} s",
    durationSeconds: "{{s}} s",
  },

  login: {
    title: "Kirjaudu sisään",
    heading: "HIL",
    prompt: "Kirjaudu sisään nähdäksesi penkit.",
    noPassword: "Tälle asennukselle ei ole määritetty salasanaa.",
    username: "Käyttäjätunnus",
    password: "Salasana",
    submit: "Kirjaudu sisään",
    submitting: "Kirjaudutaan…",
  },

  password: {
    intro:
      "Kirjauduit sisään väliaikaisella salasanalla. Vaihda se ennen kuin jatkat — mitään muuta ei voi muuttaa ennen sitä.",
    title: "Vaihda salasana",
    heading: "Valitse salasana",
    current: "Väliaikainen salasana",
    next_one: "Uusi salasana (vähintään {{count}} merkki)",
    next_other: "Uusi salasana (vähintään {{count}} merkkiä)",
    tooShort_one: "vielä {{count}} merkki",
    tooShort_other: "vielä {{count}} merkkiä",
    again: "Uusi salasana uudelleen",
    same: "täytyy poiketa väliaikaisesta",
    mismatch: "ei täsmää",
    submit: "Aseta salasana",
  },

  roles: {
    name: {
      guest: "vieras",
      operator: "operaattori",
      maintainer: "huoltaja",
      owner: "omistaja",
    },
    means: {
      guest: "lukee ajoja, raportteja ja asetuksia",
      operator: "ottaa varauksen ja jonottaa ajoja — liikuttaa laitteistoa",
      maintainer: "määrittää penkit, kierrättää tunnukset, poistaa ajoja",
      owner: "päättää kenellä muulla on pääsy",
    },
  },

  config: {
    help: {
      HIL_SSH: "testattavan näytön SSH-osoite",
      HIL_DUT_HOST: "pelkkä laitteen osoite",
      HIL_API_BASE: "AO2025-rajapinnan juuri",
      HIL_KVM: "JetKVM-osoite",
      HIL_MODEM_HOST: "modeemin hallintaliittymä",
      HIL_SIVMOCK: "tämän penkin SIV-jäljitelmä",
      HIL_CAPS: "ilmoitetut ominaisuudet",
    },
  },

  scope: {
    notRecorded: "mitkä testit: ei kirjattu",
    notRecordedTitle:
      "kirjattu ennen kuin valinta tallennettiin - kyseessä on voinut olla koko sarja tai osajoukko",
    slice: "osa testeistä",
    sliceTitle: "osa testeistä, ei kaikki",
    matching: "vain testit, jotka vastaavat ”{{filter}}”",
    onlyFile: "vain {{file}}",
    marker: "merkki: {{marker}}",
    files: "tiedostot: {{files}}",
  },

  build: {
    notRecorded: "koontia ei kirjattu",
    notRecordedTitle: "tämä ajo on vanhempi kuin koonnin raportointi harnessissa",
  },

  verdict: {
    running: "käynnissä",
    noVerdicts: "ei kirjattuja tuomioita",
    nothingRecorded: "ei mitään kirjattua",
    clean: "puhdas",
    failed_one: "{{count}} epäonnistui",
    failed_other: "{{count}} epäonnistui",
    passed_one: "{{count}} läpi",
    passed_other: "{{count}} läpi",
    naHere_one: "{{count}} ei ajettu täällä",
    naHere_other: "{{count}} ei ajettu täällä",
    xfailed_one: "{{count}} odotettu epäonnistuminen",
    xfailed_other: "{{count}} odotettua epäonnistumista",
    barLabel:
      "{{failed}} epäonnistui, {{passed}} läpi, {{skipped}} ei ajettu täällä, {{xfailed}} odotettua epäonnistumista",
    legend: {
      failed: "epäonnistui",
      xfailed: "odotettu epäonnistuminen",
      skipped: "ei ajettu täällä",
      passed: "läpi",
    },
  },

  run: {
    steps_one: "{{count}} askel",
    steps_other: "{{count}} askelta",
  },

  caps: {
    heading: "Ominaisuudet",
    none:
      "Yksikään ajo ei ole vielä raportoinut, joten näytettävää ei ole. Penkin ominaisuudet selvitetään sarjan käynnistyessä, niitä ei määritetä asetuksissa.",
    noDescription: "tälle ei tarjoiltu kuvausta",
    lastRun: "viimeisin ajo",
    reportedBy_one:
      "{{count}} ilmoitettu, lähde <runLink>{{run}}</runLink> · selvitetty koettelemalla penkkiä käynnistyksessä, ei luettu asetuksista",
    reportedBy_other:
      "{{count}} ilmoitettu, lähde <runLink>{{run}}</runLink> · selvitetty koettelemalla penkkiä käynnistyksessä, ei luettu asetuksista",
  },

  live: {
    onBench: "penkillä <benchLink>{{bench}}</benchLink>",
    step: "askel {{no}}",
    stepOf: "askel {{no}}/{{total}}",
    unknownStep: "?",
    starting: "käynnistyy — harnessin itsetestit",
    elapsedSeconds: "{{s}} s",
    elapsedMinutes: "{{m}} min",
    elapsedHours: "{{h}} h",
  },

  configPanel: {
    heading: "Asetukset",
    edit: "Muokkaa asetuksia",
    nothingSet:
      "Mitään ei ole asetettu — penkki käyttää <code>run.sh</code>:n sisäänrakennettuja oletuksia.",
    note:
      "Luetaan jokaisen ajon alussa. Komentoriviltä annettu arvo voittaa yhä, ja penkki jolla ei ole tässä merkintää käyttää <code>run.sh</code>:n oletuksia. <b>Ei salasanoja eikä API-avaimia</b> — ne pysyvät penkissä. Kuka tahansa joka voi lukea tämän penkin näkee tässä jokaisen arvon.",
    capsNone: "ei ilmoitettuja",
    capsAdd: "lisää ominaisuus ja paina Enter",
    capsRemove: "poista {{cap}}",
  },

  members: {
    heading: "Jäsenet ({{count}})",
    add: "Lisää jäsen",
    needOwner: "muutoksiin tarvitaan omistajan rooli tässä",
    emptyBench:
      "Kenellekään ei ole myönnetty oikeuksia pelkästään tähän penkkiin — pääsy tulee projektin kautta.",
    emptyProject: "Ei vielä ketään. Myönnä henkilölle tai ryhmälle rooli tässä.",
    roleFor: "rooli: {{name}}",
    remove: "poista",
    inherited: "Peritty projektista ({{count}})",
    inheritedNote:
      "Näitä muutetaan projektissa. Tässä tehty myöntö voi vain lisätä.",
    kind: { user: "Henkilö", group: "Ryhmä" },
    kindPersonTitle: "yksi henkilö",
    kindGroupTitle: "ryhmä henkilöitä",
    deactivated: "poistettu käytöstä",
    roleKeySummary: "mitä nämä roolit tarkoittavat",
    grantTo: "Myönnä",
    subjectKind: "kohteen tyyppi",
    aPerson: "henkilölle",
    aGroup: "ryhmälle",
    person: "Henkilö",
    group: "Ryhmä",
    subject: "kohde",
    choose: "valitse…",
    role: "Rooli",
    addButton: "Lisää",
  },

  auto: {
    title: "Automaattinen testaus",
    stalled: "Penkki ei ole raportoinut {{when}} jälkeen, joten mitään ei ole käynnistynyt itsestään sen jälkeen.",
    sameProduct: "{{build}} ilmestyi {{when}} samalla tuotebuildilla ({{product}}) kuin jo testattu, joten se ei käynnistänyt kampanjaa.",
    off: "Tämä penkki ei testaa mitään itsestään.",
    summary: "Tämä penkki ajaa {{what}}.",
    onNewBuild: "kampanjan jokaiselle uudelle koonnille",
    nightlyAt: "kampanjan joka yö klo {{time}}",
    and: " ja ",
    newBuildLabel: "Aja kampanja jokaiselle tälle penkille ilmestyvälle uudelle koonnille",
    nightlyLabel: "Aja kampanja joka yö klo",
    nightlyTime: "Yöajon kellonaika",
    localTime: "paikallista aikaa",
    cost: "Jokainen kampanja varaa penkin, kunnes jokainen profiili on ajettu. Se ei käynnisty, kun jonkun työ on jonossa tai käynnissä tai penkki on varattu; silloin se odottaa.",
    save: "Tallenna",
    saved: "Tallennettu.",
    waiting: "Odottaa käynnistystä: {{what}} ({{build}}) — {{why}}.",
    last: "Viimeksi itsestään käynnistetty: <campaignLink>{{what}} ({{build}})</campaignLink>, {{when}}.",
    missed: "Yön {{day}} kampanja jäi ajamatta: {{why}}.",
    installed: "asennettu koonti",
    origin: {
      new_build: "uuden koonnin kampanja",
      nightly: "yökampanja",
      manual: "kampanja",
    },
  },
  likeness: {
    flat: "Sama tulos joka ajossa: pysyvä ero, ei välkyntää.",
    title: "Samankaltaisuus {{golden}}, ajo ajolta",
    aria: "Samankaltaisuus {{golden}} {{count}} ajossa, kynnyksen kanssa",
    threshold: "kynnys {{value}}",
    point: "{{run}}: {{score}} (kynnys {{threshold}})",
    table: "Luvut",
    passed: "läpi",
    missed: "alle kynnyksen",
    near: "Meni läpi lähellä kynnystä: {{score}}, kynnys {{threshold}}, varaa {{gap}}.",
  },
  product: {
    choose: "Valitse tuote",
    panel: "Tuotteet",
    all: "Kaikki tuotteet",
    list: "Projektiluettelo",
    hint: "Jokainen kortti kertoo tuotteen uusimman tuloksen.",
    tabs: "Tässä tuotteessa",
    tabOverview: "Yleiskuva",
    tabBuilds: "Buildit",
    tabTests: "Testit",
    tabBenches: "Penkit",
    tabSettings: "Asetukset",
    search: "Hae",
    searchPlaceholder: "Tuote, penkki, sivu tai ajo",
    noHits: "Ei osumia.",
    kind: { product: "tuote", page: "sivu", bench: "penkki", run: "ajo", build: "build", case: "tapaus" },
    noBenches: "ei vielä penkkiä",
    online: "penkkejä verkossa: {{online}}/{{total}}",
    offlineFor_one: "penkki poissa {{count}} päivän",
    offlineFor_other: "penkki poissa {{count}} päivää",
    offline: "penkki poissa verkosta",
    installed: "asennettu versio",
    campRunning: "{{build}}: kampanja käynnissä",
    campGreen: "{{build}}: kaikki suunniteltu meni läpi",
    campKnown: "{{build}}: mikään uusi ei epäonnistunut; tunnettuja ongelmia: {{n}}",
    campFailed_one: "{{build}}: {{count}} testi epäonnistui",
    campFailed_other: "{{build}}: {{count}} testiä epäonnistui",
    campLost: "{{build}}: ajo ei lähettänyt tuloksiaan",
    campShort: "{{build}}: kaikkea suunniteltua ei ajettu",
    runFailed_one: "{{run}}: {{count}} testi epäonnistui",
    runFailed_other: "{{run}}: {{count}} testiä epäonnistui",
    runClean: "{{run}}: mikään ei epäonnistunut",
    nothingYet: "mitään ei ole vielä ajettu",
    empty: "Vielä ilman penkkiä ja ajoja:",
  },
  builds: {
    listTitle: "Buildit",
    title: "Build {{build}}",
    back: "buildit",
    listAnswer_one: "{{count}} build, uusin {{build}}.",
    listAnswer_other: "{{count}} buildia, uusin {{build}}.",
    listAfter: "Build on testattava tuoteversio, ei penkin levykuva. Avaa se nähdäksesi sen kampanjat ja muut ajot.",
    listNone: "Mikään tämän tuotteen ajo ei ole kertonut, mitä buildia se testasi.",
    listNoneAfter: "Ajot ovat alla. Kun jokainen ajo tallentaa buildinsa (vaihe K0 tälle tuotteelle), ne ryhmittyvät tänne buildeiksi.",
    counts: "ajoja {{runs}}, kampanjoita {{campaigns}}",
    unbuiltTitle_one: "{{count}} ajo ilman tallennettua buildia",
    unbuiltTitle_other: "{{count}} ajoa ilman tallennettua buildia",
    unbuiltNote: "Uusimmat ensin; 20 näytetään.",
    notFound: "Tuotteessa ei ole buildia {{build}}.",
    span: "ajoja {{runs}}, {{first}} – {{last}}",
    newestCampaign: "Uusin kampanja, #{{id}}",
    openCampaign: "Avaa kampanja",
    campaigns_one: "{{count}} kampanja",
    campaigns_other: "{{count}} kampanjaa",
    campaignName: "Kampanja #{{id}}",
    ranOf: "{{ran}}/{{planned}} ajettu",
    fullRuns_one: "{{count}} täysi ajo kampanjan ulkopuolella",
    fullRuns_other: "{{count}} täyttä ajoa kampanjan ulkopuolella",
    slices_one: "{{count}} ajo osalla testeistä",
    slices_other: "{{count}} ajoa osalla testeistä",
    slicesNote: "Korjausten tarkistuksia, tallennuksia, kokeiluja. Kukin kertoo vain omista tapauksistaan.",
    route: "Kampanjan pysäkit järjestyksessä",
    stopLost: "tuloksia ei saapunut",
    stopKnown: "tunnettuja {{n}}",
    stopFailed: "epäonnistui {{n}}",
    wRunning: "käynnissä",
    wGreen: "vihreä",
    wKnown: "tunnettuja ongelmia",
    wFailed_one: "{{count}} epäonnistui",
    wFailed_other: "{{count}} epäonnistui",
    wLost: "ei tuloksia",
    wShort: "kaikkea ei ajettu",
    wNone: "ei ajoja",
    wClean: "ei epäonnistumisia",
    wNoResults: "ei tuloksia",
    aRunning: "Kampanja on käynnissä.",
    aGreen: "Kaikki suunniteltu ajettiin eikä mikään epäonnistunut.",
    aKnown: "Mikään uusi ei epäonnistunut. Tunnettuja ongelmia: {{n}}.",
    aFailed_one: "Uusimmassa kampanjassa {{count}} testi epäonnistui.",
    aFailed_other: "Uusimmassa kampanjassa {{count}} testiä epäonnistui.",
    aLost: "Uusimman kampanjan ajo ei lähettänyt tuloksiaan.",
    aShort: "Uusimmassa kampanjassa kaikkea suunniteltua ei ajettu.",
    aNone: "Tällä buildilla ei ole vielä ajettu mitään.",
    aRunFailed_one: "{{run}}: {{count}} testi epäonnistui.",
    aRunFailed_other: "{{run}}: {{count}} testiä epäonnistui.",
    aRunClean: "{{run}}: mikään ei epäonnistunut.",
    startCampaign: "Käynnistä kampanja",
    report: "Tee raportti",
    images_one: "Penkin levykuva ({{count}})",
    images_other: "Penkin levykuvat ({{count}})",
    telling: "Tapaukset, jotka kertovat jotain",
    tellingNote: "kampanjassa #{{id}}",
    tellingNone: "Siellä mikään ei epäonnistunut.",
    tellKnown: "tunnettu",
    tellFailed: "epäonnistui",
    open: "Avaa",
  },
  overview: {
    leadCampaign: "Kampanja #{{id}}, {{when}}: {{ran}}/{{planned}} ajettu.",
    leadBuildRuns: "Tämän buildin ajoja {{runs}}, uusin {{when}}.",
    leadRun: "Uusin täysi ajo, {{when}}, penkillä {{bench}}",
    noBuild: "Tämän tuotteen ajot eivät vielä tallenna testaamaansa buildia (vaihe K0), joten tämä on sen uusin täysi ajo eikä build.",
    nothing: "Tällä tuotteella ei ole vielä ajettu mitään.",
    start: "Aloita testaus",
    launch: "Käännä uusi versio",
    openBuild: "Avaa build {{build}}",
    newestCampaign: "Buildin {{build}} uusin kampanja",
    openCampaign: "Avaa kampanja",
    attention: "Vaatii huomiota",
    attentionNone: "Mikään ei vaadi huomiota.",
    aLost: "ei tuloksia",
    aLostText: "{{run}} ({{stop}}) ei lähettänyt tuloksiaan",
    aQuiet: "hiljainen",
    aQuietText: "{{bench}} ei ole raportoinut {{when}} jälkeen",
    open: "Avaa",
    bench: "Penkki",
    benches_one: "{{count}} penkki",
    benches_other: "{{count}} penkkiä",
    allBenches: "Penkit",
    heldBy: "varannut {{actor}} {{purpose}}",
    settings: "Asetukset: mikä tuote on, kuka sitä tekee, mitä ajolomake tarjoaa",
  },
  productSettings: {
    back: "{{name}}",
    answer: "Mikä tämä tuote on, kuka sitä tekee ja mitä sen ajolomake tarjoaa.",
  },
  caseCtx: {
    coverageView: "Kattavuus",
    manualView: "Käsin",
    quality: "Ryhmän {{group}} kattavuus",
    stale: "Arvio on vanhentunut: ryhmän tapaukset ovat muuttuneet arvioinnin jälkeen.",
    manual: "Käsin: noin {{minutes}} min, prioriteetti {{priority}}",
    pre: "Ennen: {{text}}",
    expected: "Odota: {{text}}",
  },
  benchAdmin: {
    title: "Hallinta",
    note: "Nimeä penkki uudelleen, anna sen agentille uusi tunniste tai poista se.",
  },
  casePage: {
    crumb: "{{product}} › Testit",
    back: "testit",
    answerKnown: "Tunnettu ongelma {{date}} asti.",
    answerNone: "Ei vielä ajettu.",
    answerPassed: "Meni läpi ajossa {{run}}.",
    answerFailed: "Epäonnistui ajossa {{run}}.",
    answerOther: "{{run}}: {{verdict}}.",
    expects: "Mitä se odottaa, verrattuna ajossa {{run}}",
    mask: "maski {{areas}} alueella, {{pct}} % ruudusta",
    likeness: "Samankaltaisuus referenssiin, ajo ajolta",
    does: "Mitä se tekee",
    group: "Ryhmä",
    steps: "Askeleet",
    requires: "Tarvitsee",
    coverage: "Kattavuus ja käsin",
    grade: "Ryhmän arvosana",
    byHand: "Käsin",
    byHandValue: "noin {{minutes}} min, prioriteetti {{priority}}",
    expect: "Odota",
    noCoverage: "Tapaukselle ei ole arviota eikä käsin tehtävää menettelyä.",
    history: "Tulokset, uusimmat ensin",
    definition: "Määritelmä kirjoitettuna",
  },
  buildPicker: {
    title: "DisplayOS-käännös ja testit",
    explain: "Jokainen penkin levykuva sisältää yhden DisplayOS-käännöksen ja sillä ajettavat testit. Penkki vaihtaa valittuun levykuvaan ennen ajoa; jos se ei onnistu, mitään ei ajeta ja työ kertoo syyn.",
    nowIs: "Penkillä nyt: {{build}}, testit {{tests}}.",
    nowBy: "Vaihdon teki",
    nowByRun: "Vaihdon teki {{run}}, {{when}}.",
    nowCaveat: "Penkillä käsin tehty vaihto ei näy tässä.",
    nowUnknown: "Täällä ei tiedetä, mikä levykuva penkillä on: mikään hil-centralin ajo ei ole vielä vaihtanut sitä.",
    keep: "Pidä penkillä oleva",
    keepNow: "Ei vaihtoa: ajetaan {{build}}, testit {{tests}}, kuten nyt.",
    keepWhy: "Ei vaihtoa. Ajetaan penkillä nyt olevalla levykuvalla.",
    tests: "Testit {{sha}}",
    scripts: "penkin skriptit {{sha}}",
    pointer: "{{tag}}",
    pointerWhy: "toinen nimi jollekin yllä olevista; mille, ei tiedetä",
    oldShape: "vanhalla tavalla nimetty: nimi ei kerro testejä",
    newest: "uusin · yöajo käyttää tätä",
    onBenchNow: "penkillä nyt",
    olderTests: "vanhemmat testit",
    download: "ladattava · minuutteja",
    lastUsed: "viimeksi {{when}}, {{what}}",
    neverUsed: "ei vielä käytetty hil-centralista",
    campaign: "kampanja #{{id}}",
    olderWarning: "Tässä levykuvassa on vanhemmat testit kuin saman DisplayOS-käännöksen uusimmassa. Tapaukset ja testidata voivat erota nykyisistä, joten ero tuloksissa voi johtua testeistä eikä DisplayOSista.",
    registry_one: "{{count}} levykuva rekisterissä (ladattava)",
    registry_other: "{{count}} levykuvaa rekisterissä (ladattava)",
    other_one: "{{count}} muu nimi penkillä",
    other_other: "{{count}} muuta nimeä penkillä",
    typeIt: "Kirjoita levykuvan tunniste",
  },
  runAbout: {
    line_one: "Tarkistaa {{count}} tapausryhmän: {{names}}.",
    line_other: "Tarkistaa {{count}} tapausryhmää: {{names}}.",
    lineMore_one: "Tarkistaa {{count}} tapausryhmän: {{names}} ja {{more}} muuta.",
    lineMore_other: "Tarkistaa {{count}} tapausryhmää: {{names}} ja {{more}} muuta.",
    howReplay: "Toistaa tallennettua OBA-liikennettä, näkymässä {{layout}}.",
    howSimulated: "Ohjaa näkymää {{layout}} OBA Emulatorin simuloidulla matkalla.",
    howFixture: "Ohjaa testikehyksen omaa testinäkymää (hil_fixture) OBA Emulatorin simuloidulla matkalla.",
    howOther: "Datatila {{mode}}, näkymässä {{layout}}.",
    theLayout: "asiakkaan näkymä",
    checks: "Mitä se tarkistaa:",
    cases_one: "{{count}} tapaus",
    cases_other: "{{count}} tapausta",
    failed_one: "{{count}} epäonnistui",
    failed_other: "{{count}} epäonnistui",
    more_one: "{{count}} ryhmä lisää",
    more_other: "{{count}} ryhmää lisää",
    fewer: "vähemmän",
  },
  known: {
    status: "tunnettu",
    short: "Tunnettu ongelma {{date}} asti.",
    shared_one: "{{count}} tunnettu ongelma: {{reason}}",
    shared_other: "{{count}} tunnettua ongelmaa, kaikilla sama syy: {{reason}}",
    title: "Tunnetut ongelmat",
    intro: "Epäonnistuvat tapaukset, joille joku on kirjannut syyn, päivämäärään asti. Ajoissaan ne ovat yhä epäonnistumisia; kampanjat ja Now laskevat ne erikseen, jotta uusi epäonnistuminen erottuu.",
    none: "Ei tunnettuja ongelmia.",
    onlyKnown: "Mikään uusi ei epäonnistunut. Tunnetut ongelmat: {{n}}.",
    onlyKnownNewest: "Uusimmassa kampanjassa mikään uusi ei epäonnistunut. Tunnetut ongelmat: {{n}}.",
    also: "Tunnetut ongelmat: {{n}}.",
    word: "tunnetut ongelmat",
    onStop: "Tunnetut ongelmat tässä: {{list}}.",
    chip: "Tunnettu ongelma {{date}} asti: {{reason}}",
    by: "asetti {{who}}",
    mark: "Merkitse tunnetuksi ongelmaksi",
    reason: "Miksi se epäonnistuu",
    until: "Asti",
    save: "Tallenna",
    cancel: "Peruuta",
    end: "Päätä",
    stateOpen: "{{date}} asti",
    stateExpired: "vanhentui {{date}}",
    closedPassed: "päättyi {{date}}: tapaus meni läpi",
    closedRemoved: "päättyi {{date}}, päätti {{who}}",
  },
  campaigns: {
    title: "Kampanjat",
    heading: "Kampanjat",
    answerNone: "Täällä ei ole vielä ajettu yhtään kampanjaa.",
    answerGreen: "Uusin kampanja ajoi kaiken suunnitellun, eikä mikään epäonnistunut.",
    answerRunning: "Penkillä {{bench}} on kampanja käynnissä.",
    answerCancelled: "Uusin kampanja peruttiin ennen kuin kaikki ajettiin.",
    answerFailed_one: "Uusimmassa kampanjassa {{count}} testi epäonnistui.",
    answerFailed_other: "Uusimmassa kampanjassa {{count}} testiä epäonnistui.",
    answerNotAllRan: "Uusin kampanja ajettiin, mutta kaikkea suunniteltua ei ajettu.",
    answerLost: "Uusimman kampanjan ajo pysähtyi ennen kuin testit raportoivat.",
    loaded_one: "{{count}} kampanja, uusin ensin.",
    loaded_other: "{{count}} kampanjaa, uusin ensin.",
    projectViews: "Mikä projekti",
    outcomeViews: "Mikä tulos",
    allProjects: "Kaikki projektit",
    any: "Mikä tahansa tulos",
    onlyNotGreen: "Ei vihreä",
    onlyGreen: "Vihreä",
    onlyRunning: "Käynnissä",
    oneBench: "Vain penkin {{bench}} kampanjat.",
    showEveryBench: "Näytä kaikki penkit",
    empty: "Täällä ei ole vielä ajettu yhtään kampanjaa. Kampanja ajaa penkin jokaisen profiilin peräkkäin.",
    noneMatch: "Mikään kampanja ei vastaa tätä.",
    buildGroup_one: "Koonti {{build}} · {{count}} kampanja",
    buildGroup_other: "Koonti {{build}} · {{count}} kampanjaa",
    installedGroup_one: "Penkille {{bench}} asennettuna ollut · {{count}} kampanja",
    installedGroup_other: "Penkille {{bench}} asennettuna ollut · {{count}} kampanjaa",
    rowTitle: "Kampanja #{{id}} penkillä {{bench}}",
    loadMore: "Näytä {{count}} lisää",
    filterNote_one: "Suodatettu tällä sivulla, {{count}} ladatusta kampanjasta.",
    filterNote_other: "Suodatettu tällä sivulla, {{count}} ladatusta kampanjasta.",
    origin: {
      manual: "käynnisti {{who}}",
      new_build: "käynnistyi itsestään, uusi koonti",
      nightly: "käynnistyi itsestään, yöllä",
    },
  },
  campaign: {
    badge: "Kampanja #{{id}}",
    stops_one: "{{count}} pysähdys",
    stops_other: "{{count}} pysähdystä",
    routeHeading: "Ajot",
    why: "Miksi: {{reason}}",
    notFound: "Kampanjaa #{{id}} ei ole. Kampanjat-sivu listaa ne kaikki.",
    report: "Tee raportti tästä kampanjasta",
    origin: {
      new_build: "käynnistyi itsestään uuden koonnin ilmestyttyä, {{when}}",
      nightly: "käynnistyi itsestään yöllä, {{when}}",
    },
    loading: "Ladataan kampanjaa…",
    back: "← {{bench}}",
    title: "Kampanja penkillä {{bench}}",
    build: "koonti {{build}}",
    installed: "jo asennettu koonti",
    startedBy: "aloitti {{who}} {{when}}",
    answerGreen: "Kaikki suunniteltu ajettiin, eikä mikään epäonnistunut.",
    answerRunning: "Käynnissä {{n}}/{{of}}: {{label}}.",
    answerWaiting: "Odotetaan, että penkki aloittaa seuraavan ajon.",
    answerCancelled: "Peruttu ennen kuin kaikki ajettiin.",
    answerFailed_one: "{{count}} testi epäonnistui.",
    answerFailed_other: "{{count}} testiä epäonnistui.",
    answerNotAllRan: "Mikään ei epäonnistunut, mutta kaikkea suunniteltua ei ajettu.",
    answerLost: "Ajo pysähtyi ennen kuin testit raportoivat, joten osalla kampanjaa ei ole tulosta.",
    stopLost: "Tämä ajo pysähtyi ennen kuin testit raportoivat. Tapauksia, joiden tarkistus epäonnistui: {{n}}.",
    imageDetails: "Penkin levykuva",
    planned_one: "{{count}} suunniteltu",
    planned_other: "{{count}} suunniteltu",
    ran_one: "{{count}} ajettu",
    ran_other: "{{count}} ajettu",
    passed_one: "{{count}} läpi",
    passed_other: "{{count}} läpi",
    expected_one: "{{count}} odotettu epäonnistuminen",
    expected_other: "{{count}} odotettua epäonnistumista",
    failed_one: "{{count}} epäonnistui",
    failed_other: "{{count}} epäonnistui",
    notRun_one: "{{count}} ei ajettu",
    notRun_other: "{{count}} ei ajettu",
    routeLabel: "Tämän kampanjan ajot järjestyksessä",
    attempt: "yritys {{n}}",
    openRun: "Avaa ajo",
    retry: "Aja uudelleen",
    retried: "Jonossa uudelleen. Vaihe näyttää uuden yrityksen.",
    cancel: "Peru kampanja",
    cancelled_one: "Peruttiin {{count}} ajo.",
    cancelled_other: "Peruttiin {{count}} ajoa.",
    recent: "Viimeisimmät kampanjat tällä penkillä",
    listItem: "Kampanja {{when}}: {{ran}}/{{planned}} ajettu",
    state: {
      queued: "odottaa",
      claimed: "käynnistyy",
      running: "käynnissä",
      done: "valmis",
      failed: "ei valmistunut",
      cancelled: "peruttu",
      missing: "ei jonossa",
    },
    status: {
      running: "käynnissä",
      green: "kaikki ajettiin, mikään ei epäonnistunut",
      not_green: "ei kaikki vihreää",
      cancelled: "peruttu",
    },
  },
  start: {
    everyProfile_one: "Jokainen profiili: {{count}} ajo, {{cases}} tapausta",
    everyProfile_other: "Jokainen profiili: {{count}} ajoa, {{cases}} tapausta",
    everyProfileCost: "Varaa penkin, kunnes jokainen profiili on ajettu peräkkäin.",
    campaignStarted: "Kampanja aloitettiin. <campaignLink>Seuraa sitä ajojensa reittinä.</campaignLink>",
    title: "Aloita testaus",
    bench: "Penkki",
    benchOffline: "{{bench}} — ei raportoi",
    benchHeld: "{{bench}} — varaaja {{actor}}",
    benchReadOnly: "{{bench}} — voit vain katsoa",
    why: "Miksi",
    queued: "Lisätty jonoon penkille <benchLink>{{bench}}</benchLink>. Penkin sivu näyttää sen jonossa ja käynnissä.",
    none: "Testattavaa penkkiä ei vielä ole.",
    loading: "Ladataan penkkejä…",
  },
  runPanel: {
    profile: "Mitä testataan",
    profileCases_one: "{{count}} tapaus",
    profileCases_other: "{{count}} tapausta",
    moreOptions: "Lisäasetukset: valitse testit itse",
    heading: "Aloita testaus",
    markers: {
      auto: "auto — täysin automaattinen",
      screenshot: "screenshot_partial — visuaaliset tarkistukset",
      both: "auto + screenshot_partial",
    },
    running: "Käynnissä",
    queued: "Jonossa",
    askedBy: "pyytäjä {{who}} · {{when}}",
    stop: "Pysäytä",
    stopTitle: "ajon pysäyttäminen vaatii operaattorin roolin tässä",
    stopping:
      "Penkkiä pyydettiin pysähtymään. Se päättää sarjan seuraavassa ilmoittautumisessa, minuutin sisällä.",
    removed: "Poistettu jonosta.",
    cancelNote:
      "Peruminen kirjaa päätöksen; se ei ulotu penkkiin pysäyttämään sarjaa.",
    waiting: "Jonossa ({{count}})",
    remove: "poista",
    leaseHeld:
      "<b>{{actor}}</b> pitää tätä penkkiä. Ajo ei voi alkaa ennen kuin varaus päättyy — penkki hylkäisi sen joka tapauksessa.",
    leaseHeldFor:
      "<b>{{actor}}</b> pitää tätä penkkiä: {{purpose}}. Ajo ei voi alkaa ennen kuin varaus päättyy — penkki hylkäisi sen joka tapauksessa.",
    tests: "Testiryhmä",
    jobMarked: "testit merkillä ”{{marker}}”",
    jobMatching: "jotka vastaavat ”{{filter}}”",
    jobBuild: "versio {{build}}",
    onlyMatching: "Vain osuvat",
    filterPlaceholder: "esim. LED (valinnainen)",
    reasonPlaceholder:
      "Miksi — seuraava lukee tämän, kuten varauksen syyn",
    queueing: "Aloitetaan…",
    start: "Aloita testaus",
    needOperator:
      "Ajon jonottaminen liikuttaa laitteistoa; se vaatii operaattorin roolin tässä penkissä.",
    takesDut: "Varaa penkin testien ajaksi — täydellä sarjalla tunteja.",
    installedVersion:
      "Testaa penkille jo asennetun käännöksen. Jos versiota ei ole vielä käännetty, käännä se ensin <projectLink>projektissa</projectLink>.",
    installedVersionNoProject:
      "Testaa penkille jo asennetun käännöksen. Penkki ei kuulu projektiin, joten uutta versiota ei ole mistä kääntää.",
    buildRef: "Ensin asennettava käännös (valinnainen)",
    buildRefPlaceholder: "tyhjä: testaa penkillä jo olevan version",
    buildRefList: "Jo käännetty: valittu versio asennetaan ilman käännöstä.",
    buildRefNote: "Tämä on pyyntö; ajo kirjaa sen, mikä todella asentui. Jos asennus ei onnistu, testejä ei ajeta lainkaan ja työ päättyy virheeseen syyn kanssa.",
    offline: "Penkin agentti ei raportoi; kukaan ei poimisi tätä.",
    earlier: "aiemmin pyydetyt ajot ({{count}})",
    jobState: {
      queued: "jonossa",
      claimed: "poimittu",
      running: "käynnissä",
      done: "valmis",
      failed: "epäonnistui",
      cancelled: "peruttu",
    },
  },

  markdown: {
    empty: "Tähän ei ole vielä kirjoitettu mitään.",
    showAll: "Näytä kaikki",
    showLess: "Näytä vähemmän",
    label: "Markdown",
    tabWrite: "kirjoita",
    tabSplit: "rinnakkain",
    tabPreview: "esikatselu",
    placeholder: "# Otsikko\n\nOsoitteet, erikoisuudet, mihin tämä on kytketty…",
    previewEmpty: "Ei vielä esikatseltavaa.",
    dirty: "tallentamattomia muutoksia",
    saved: "tallennettu",
    shortcuts: "⌘/Ctrl+Enter tallentaa · Esc peruu",
  },

  history: {
    heading: "Historia",
    scopeAll: "Kaikki ajot",
    scopeFull: "Koko sarja",
    scopePartial: "Osajoukot",
    chart: "kaavio",
    table: "taulukko",
    unknownTitle:
      "kirjattu ennen kuin run.sh tallensi valinnan, joten ne eivät kuulu kumpaankaan ryhmään",
    unknownCount: "· {{count}} tuntematonta laajuutta",
    tooFewFull:
      "Alle kaksi koko sarjan ajoa verrattavaksi. Valitse toinen suodatin nähdäksesi loput.",
    tooFewPartial:
      "Alle kaksi osittaista ajoa verrattavaksi. Valitse toinen suodatin nähdäksesi loput.",
    colRun: "Ajo",
    colFailed: "Epäonnistui",
    colXfailed: "Odotetut epäonnistumiset",
    colSkipped: "Ohitettu",
    colPassed: "Läpi",
    colCases: "Tapaukset",
    chartLabel: "tapaukset lopputuloksen mukaan ajoa kohti",
    trackTitle: "{{name}} — {{count}} tapausta",
    segTitle: "{{name}} — {{count}} {{outcome}} · {{pct}} % / {{total}}",
    axisRun: "ajo",
    legendTrack: "tapaukset ajossa",
    legendNote: "yksi pylväs ajoa kohti · avaa napsauttamalla",
  },

  audit: {
    heading: "Penkin aikajana",
    loading: "Ladataan auditointia…",
    heldNowBy: "pitää nyt <b>{{actor}}</b>",
    heldNowByFor: "pitää nyt <b>{{actor}}</b> — {{purpose}}",
    lapsed:
      "vapaa — <b>{{actor}}</b>:n varaus raukesi {{when}} ilman vapautusta",
    free: "vapaa — kukaan ei pidä tätä penkkiä",
    leasesOnly: "vain varaukset ja ajot — pääsymerkintöjä ei ole vielä toimitettu",
    neverReported:
      "ei koskaan raportoinut — tätä penkkiä ei auditoida, se ei ole jouten",
    lastAccess: "viimeisin pääsy {{when}}",
    looseWrites_one: "{{count}} kirjoitus ilman omaa varausta",
    looseWrites_other: "{{count}} kirjoitusta ilman omaa varausta",
    noPurpose: "syytä ei annettu",
    legendHeld: "varattu (sävyt vuorottelevat erottamaan varaukset)",
    legendWrite: "kirjoitus",
    legendRead: "luku",
    legendTaken: "otettu haltuun",
    legendDenied: "evätty",
    colWhen: "milloin",
    colKind: "laji",
    colWhat: "mitä",
    colWho: "kuka",
    colDetail: "yksityiskohdat",
    noLease: "· ei varausta",
    empty: "Tälle penkille ei ole vielä kirjattu mitään.",
    kind: { lease: "varaus", access: "pääsy", run: "ajo" },
    event: {
      acquired: "otettu",
      released: "vapautettu",
      expired: "rauennut",
      taken: "otettu haltuun",
      denied: "evätty",
      peek: "kurkistus",
      running: "käynnissä",
    },
  },

  people: {
    accountCount_one: "{{count}} tili",
    accountCount_other: "{{count}} tiliä",
    groupCount_one: "{{count}} ryhmä",
    groupCount_other: "{{count}} ryhmää",
    title: "Käyttäjät",
    heading: "Käyttäjät",
    tabAccounts: "Tilit",
    tabGroups: "Ryhmät",
    intro:
      "Roolit myönnetään projektissa tai penkissä, kyseisen projektin tai penkin omalta sivulta. Tämä sivu kertoo keitä on olemassa ja kuka kuuluu mihinkin tiimiin.",
    passwordFor: "Salasana käyttäjälle <b>{{who}}</b>: <code>{{pw}}</code>",
    passwordOnce:
      "Näytetään kerran — se tallennetaan tiivisteenä, joten tämä on ainoa kopio. Häntä pyydetään vaihtamaan se ensimmäisellä kirjautumisella.",
    newAccount: "Uusi tili",
    username: "Käyttäjätunnus",
    usernamePlaceholder: "dgugala",
    fullName: "Koko nimi",
    fullNamePlaceholder: "Dariusz Gugała",
    instanceAdmin: "instanssin ylläpitäjä",
    create: "Luo",
    createNote:
      "Salasana luodaan ja näytetään kerran. Tilin on vaihdettava se ensimmäisellä kirjautumisella.",
    badgeAdmin: "ylläpitäjä",
    badgeSso: "sso",
    badgeDeactivated: "poistettu käytöstä",
    hide: "piilota",
    reach: "mihin hän pääsee",
    resetPassword: "nollaa salasana",
    makeAdmin: "tee ylläpitäjäksi",
    revokeAdmin: "poista ylläpito",
    deactivate: "poista käytöstä",
    reactivate: "ota käyttöön",
    lastSignedIn: "viimeksi kirjautunut {{when}}",
    inGroups_one: " · {{count}} ryhmässä",
    inGroups_other: " · {{count}} ryhmässä",
    mustChange: " · salasana on vaihdettava",
    noAccounts:
      "Ei vielä tilejä. Niin kauan kuin niitä ei ole, tämä instanssi on avoin (tai jaetun aloitussalasanan takana) — ensimmäisen tilin luominen sulkee sen.",
    noGrants:
      "Ei myöntöjä. Hän voi silti lukea jokaisen sisäiseksi merkityn projektin.",
    grantOn: "<b>{{role}}</b> kohteessa <scopeLink>{{scope}}</scopeLink>",
    throughGroup: " — ryhmän {{group}} kautta",
    grantedDirectly: " — myönnetty suoraan",
    newGroup: "Uusi ryhmä",
    groupNamePlaceholder: "sncf-testers",
    noGroups:
      "Ei vielä ryhmiä. Ryhmä on tiimi: laita siihen ihmisiä ja myönnä sitten ryhmälle rooli projektissa.",
    memberCount_one: "{{count}} jäsen",
    memberCount_other: "{{count}} jäsentä",
    grantCount_one: "{{count}} myöntö",
    grantCount_other: "{{count}} myöntöä",
    groupPeople: "Henkilöt",
    manager: "vastuuhenkilö",
    remove: "poista",
    groupEmpty: "Tässä ryhmässä ei ole vielä ketään.",
    addToGroup: "lisää ryhmään",
    addSomebody: "lisää joku…",
    add: "Lisää",
    whereRole: "Missä tällä ryhmällä on rooli",
    noWhere: "Ei vielä missään. Myönnä se projektin tai penkin sivulta.",
    groupNote:
      "Jokainen tässä ryhmässä saa {{what}} kaikkialla, missä ryhmälle on myönnetty rooli. Roolit: {{ladder}}.",
    whatAbove: "yllä olevat roolit",
    whatWhatever: "sen mitä ryhmälle myönnetään",
  },

  now: {
    answerAttention_one: "{{count}} asia vaatii huomiota.",
    answerAttention_other: "{{count}} asiaa vaatii huomiota.",
    answerRunning_one: "{{count}} ajo on käynnissä.",
    answerRunning_other: "{{count}} ajoa on käynnissä.",
    onlineCount: "{{count}} verkossa",
    lastRun: "Viimeisin ajo alkoi {{when}}.",
    onlineFree: "verkossa, vapaa",
    onlineHeld: "verkossa, varattu",
    answerQuiet: "Mitään ei ole käynnissä eikä mikään ole juuri hajonnut.",
    headingBuilds: "Koonnit",
    buildNamed: "Koonti {{build}}",
    installedOn: "Penkin {{bench}} asennettu koonti",
    buildGreen: "{{build}}: {{planned}} suunniteltu, {{ran}} ajettu, mikään ei epäonnistunut.",
    buildRunning: "{{build}}: kampanja käynnissä, {{ran}}/{{planned}} ajettu.",
    buildCancelled: "{{build}}: kampanja peruttiin, kun {{ran}}/{{planned}} oli ajettu.",
    buildFailed_one: "{{build}}: {{count}} testi epäonnistui ({{ran}}/{{planned}} ajettu).",
    buildFailed_other: "{{build}}: {{count}} testiä epäonnistui ({{ran}}/{{planned}} ajettu).",
    buildNotAllRan: "{{build}}: mikään ei epäonnistunut, mutta vain {{ran}}/{{planned}} suunnitellusta ajettiin.",
    buildLost: "{{build}}: ajo pysähtyi ennen kuin testit raportoivat ({{ran}}/{{planned}} ajettu).",
    buildOnlyKnown: "{{build}}: mikään uusi ei epäonnistunut ({{ran}}/{{planned}} ajettu). Tunnetut ongelmat: {{n}}.",
    openCampaign: "Avaa kampanja",
    title: "Nyt",
    heading: "Nyt",
    rigs_one: "{{count}} penkki",
    rigs_other: "{{count}} penkkiä",
    running: "{{count}} käynnissä",
    idle: "jouten",
    headingRunning: "Käynnissä",
    headingAttention: "Vaatii huomiota",
    headingRigs: "Penkit",
    allQuiet:
      "Mitään ei ole käynnissä eikä mikään ole juuri hajonnut. Penkit ja niiden viimeisimmät ajot ovat alla.",
    noBenches: "Ei vielä penkkejä. Luo yksi ja rekisteröi sitten sen agentti.",
    brokeLabel_one: "{{count}} uusi epäonnistuminen",
    brokeLabel_other: "{{count}} uutta epäonnistumista",
    brokeTitle: "<runLink>{{run}}</runLink> penkissä {{bench}} · {{when}}",
    gateLabel: "harnessin portti epäonnistui",
    gateTitle:
      "<runLink>{{run}}</runLink> mitattu harnessilla joka ei pysy kasassa",
    noJunitLabel: "tulokset puuttuvat",
    noJunitTitle:
      "<runLink>{{run}}</runLink> päättyi ilman tuomioiden lähetystä, joten sen luvut ovat epäonnistuneita tarkistuksia",
    leaseLabel: "varaus yli määräajan",
    leaseTitle: "<b>{{actor}}</b> pitää yhä penkkiä <benchLink>{{bench}}</benchLink>",
    leaseTitleFor:
      "<b>{{actor}}</b> pitää yhä penkkiä <benchLink>{{bench}}</benchLink> — {{purpose}}",
    quietLabel: "hiljaa",
    quietAuto: "Automaattinen testaus on täällä päällä, joten sen kampanjatkin ovat pysähtyneet.",
    quietTitle:
      "<benchLink>{{bench}}</benchLink> ei ole raportoinut sitten {{when}}",
    selfTests: "harnessin itsetestit",
    stateOnline: "verkossa",
    whyOnline: "agentti nähty viimeisen 2 minuutin aikana",
    statePublishes: "vain julkaisee",
    whyPublishes:
      "yksikään agentti ei ole koskaan raportoinut tänne: ajot julkaistaan, mutta mitään ei voi lähettää sille",
    stateOffline: "poissa",
    whyOffline: "ei tuoretta sykettä",
    heldBy: "pitäjä <b>{{actor}}</b>",
    heldByFor: "pitäjä <b>{{actor}}</b> — {{purpose}}",
    free: "vapaa",
    seen: "nähty {{when}}",
    neverSeen: "ei koskaan nähty",
    nothingReported: "Ei vielä raportoitua.",
    runsLink: "Ajot",
    takeAndRun: "Aloita testaus",
  },

  projects: {
    campaignsHeading: "Kampanjat",
    allCampaigns: "Tämän projektin kaikki kampanjat",
    noCampaigns: "Tässä projektissa ei ole vielä ajettu yhtään kampanjaa.",
    projectCount_one: "{{count}} projekti",
    projectCount_other: "{{count}} projektia",
    title: "Projektit",
    heading: "Projektit",
    newProject: "Uusi projekti",
    namePlaceholder: "Projektin nimi",
    create: "Luo",
    emptyAdmin: "Ei vielä projekteja. Luo yksi ja laita siihen penkkejä.",
    emptyReader:
      "Täällä ei ole vielä sinulle mitään — pyydä roolia johonkin projektiin tai sen penkkiin.",
    privateBadge: "Yksityinen",
    privateTitle: "vain jäsenet näkevät tämän projektin",
    yourRoleShort: "sinä: {{role}}",
    benchCount_one: "{{count}} penkki",
    benchCount_other: "{{count}} penkkiä",
    runCount_one: "{{count}} ajo",
    runCount_other: "{{count}} ajoa",
    backToProjects: "← projektit",
    fallbackTitle: "Projekti {{id}}",
    yourRole: "roolisi: {{role}}",
    editDescription: "Muokkaa kuvausta",
    scenariosLink: "Skenaariot",
    descriptionLabel: "Projektin kuvaus",
    noDescription:
      "Ei vielä kuvausta. Kerro mikä tämä projekti on, missä sen penkit ovat ja mitä uuden tulijan pitää tietää.",
    benchesHeading: "Penkit ({{count}})",
    noBenches: "Ei vielä yhtään — luo penkki Penkit-sivulta ja liitä se tähän.",
    lastSeen: "viimeksi nähty {{when}}",
    visPrivate: "yksityinen — vain jäsenet näkevät sen",
    visInternal: "sisäinen — jokainen kirjautunut voi lukea sen",
    makeInternal: "{{label}} · muuta sisäiseksi",
    makePrivate: "{{label}} · muuta yksityiseksi",
  },

  scenarios: {
    viewCases: "Kaikki tapaukset",
    viewLabel: "mitä näistä skenaarioista näytetään",
    answerCases_one: "{{count}} testitapaus.",
    answerCases_other: "{{count}} testitapausta.",
    heading: "Skenaariot",
    pageTitle: "Skenaariot · {{project}}",
    back: "← {{project}}",
    empty:
      "Mikään tämän projektin penkkien ajo ei ole vielä kirjannut skenaarioitaan. Lista ilmestyy ensimmäisen ne lähettävän ajon jälkeen.",
    openNotHere: "{{id}} ei ole tällä listalla. Valitse toinen lista yllä.",
    buildUnknown: "build tuntematon",
    lists_one: "{{count}} lista — yksi testidatan versiota kohden",
    lists_other: "{{count}} listaa — yksi testidatan versiota kohden; uusin on auki",
    fromYourRun: "sinun ajosi",
    asOf: "Ajon {{run}} kirjaama penkillä {{bench}}, {{when}}, build {{build}}.",
    openRun: "avaa ajo",
    testData: "testidata {{digest}}",
    noFingerprint: "ei testidatan tunnistetta",
    against: "Verrattuna ajoon {{run}}: lisätty {{added}}, poistettu {{removed}}.",
    againstSame: "Samat tapaukset kuin ajossa {{run}}.",
    outsidePlan: "Ei suunnitelmassa",
    filter: "Suodata: tunniste, nimi, verbi, vaatimus",
    shown: "näkyvissä {{shown}}/{{count}}",
    never: "{{bench}}: ei koskaan raportoitu",
    lastOn: "{{bench}}: {{verdict}} ajossa {{run}}",
    lastRan: "viimeksi ajettu ajossa {{run}}: {{verdict}}",
    ranBefore: "aiemmin {{verdict}}",
    draft: "luonnos",
    requires: "vaatii: {{list}}",
    tree: {
      heading: "Asiakkaan ja näytön mukaan",
      intro:
        "Jokainen profiili, jonka penkki valmistelee, ja mitä sen uusin ajo sanoi: ensin yleiset testit, sitten jokainen asiakas ja sen näytöt. Ajon alla ovat sen arvioimat tapaukset, joten jokainen tapaus on yhdessä paikassa.",
      general: "Yleiset testit",
      recordedJourney: "Tallennettu matka",
      storyboards: "Kuvakäsikirjoitukset",
      simulated: "Simuloitu data",
      fixture: "Fixture",
      cases_one: "{{count}} tapaus",
      cases_other: "{{count}} tapausta",
      onBench: "penkillä {{bench}}",
      never: "Ei ole vielä ajettu täällä.",
      last: "{{run}} buildilla {{build}}, {{when}}: {{result}}.",
      result: "{{passed}} läpi, {{failed}} epäonnistui, {{xfailed}} odotettua epäonnistumista, {{skipped}} ei ajettu",
      campaignGreen: "kaikki ajettiin, mikään ei epäonnistunut",
      campaignNotGreen: "ei kaikki vihreää",
      campaignRunning: "käynnissä",
      campaignCancelled: "peruttu",
      show: "näytä tapaukset",
      hide: "piilota tapaukset",
      notInAny_one: "{{count}} alla olevan listan tapaus ei ole vielä missään haarassa: {{list}}",
      notInAny_other: "{{count}} alla olevan listan tapausta ei ole vielä missään haarassa: {{list}}",
      inSeveral: "Useammassa kuin yhdessä haarassa: {{list}}",
    },
    plan: {
      objective: "tavoite",
      preconditions: "esiehdot",
      expected_result: "odotettu tulos",
    },
    asWritten: "kirjoitettuna · {{source}}",
    noSource: "Tämä ajo ei kirjannut tapauksen tekstiä.",
    alsoSame: "Sama teksti: {{source}}.",
    alsoDiffers: "Eri teksti: {{source}}.",
    notRecorded: "Millään tämän sivun listalla ei ole tämän tapauksen tekstiä.",
    history: "tulokset penkeittäin, uusin ensin",
    expected: "Mitä se odottaa, verrattuna ajossa {{run}}",
    expectedAlt: "Hyväksytty näkymä {{golden}}",
    noHistory: "ei koskaan raportoitu",
  },

  scenarioView: {
    readable: "Luettava",
    source: "Lähde",
    precondition: "Ennen vaiheita",
    restore: "Lopuksi palauttaa",
    captured: "tallennettu ensin: {{what}}",
    checks: "Tarkistaa",
    saves: "Tallentaa",
    root: "rootina",
    draft: "luonnos",
    runsLast: "ajetaan viimeisenä",
    script_one: "skripti, {{count}} rivi",
    script_other: "skripti, {{count}} riviä",
    rects_one: "{{count}} suorakulmio",
    rects_other: "{{count}} suorakulmiota",
    unplaced: "Tämän lohkon kommentteja ei voitu varmasti yhdistää vaiheisiin, joten ne näkyvät vain lähteessä.",
  },
  benches: {
    title: "Penkit",
    heading: "Penkit",
    newBench: "Uusi penkki",
    tokenNote:
      "<b>{{name}}</b>-tunnus — kopioi se nyt, sitä ei näytetä uudelleen ja vain sen tiiviste tallennetaan.",
    dismiss: "sulje",
    empty: "Ei vielä penkkejä. Luo yksi ja rekisteröi sitten sen agentti.",
    noTokenBadge: "ei tunnusta",
    noTokenTitle: "tälle penkille ei ole vielä myönnetty tunnusta",
    heldBy: "pitäjä <b>{{actor}}</b>",
    heldByFor: "pitäjä <b>{{actor}}</b> — {{purpose}}",
    lastSeen: "viimeksi nähty",
    runs: "ajot",
    running: "käynnissä",
    agent: "agentti",
    open: "Avaa",
    runsLink: "Ajot",
    edit: "Muokkaa",
    newToken: "Uusi tunnus",
    getToken: "Hae tunnus",
    editTitle: "Muokkaa: {{name}}",
    deleteTitle: "Poista {{name}}",
    onlineTitle: "agentti nähty viimeisen 2 minuutin aikana",
    offlineTitle: "ei tuoretta sykettä",
    name: "Nimi",
    description: "Kuvaus",
    deleteWarn_one:
      "Tämä poistaa myös <b>{{count}}</b> sille kirjatun ajon. Tätä ei voi perua. Kirjoita <name>{{name}}</name> vahvistaaksesi.",
    deleteWarn_other:
      "Tämä poistaa myös <b>{{count}}</b> sille kirjattua ajoa. Tätä ei voi perua. Kirjoita <name>{{name}}</name> vahvistaaksesi.",
  },

  benchDetail: {
    qualityLink: "Mitä sen testit todistavat",
    answerFree: "Vapaa ja verkossa.",
    answerHeld: "Varannut {{actor}}.",
    fallbackTitle: "Penkki {{id}}",
    back: "← penkit",
    yourRole: "roolisi: {{role}}",
    yourRoleTitle: "tässä penkissä, suoraan tai sen projektin kautta",
    factSeen: "nähty",
    factAgent: "agentti",
    factRuns: "ajot",
    factCaps: "ominaisuudet",
    factSince: "alkaen",
    factRunning: "käynnissä",
    never: "ei koskaan",
    noCapsReported: "ei raportoitu",
    nothingReported: "Ei vielä raportoitua.",
    colRun: "Ajo",
    colStarted: "Alkoi",
    colTook: "Kesto",
    colSteps: "Askelia",
    colResult: "Tulos",
    allRuns: "Kaikki tämän penkin ajot",
    allRunsTail: " — suodattimineen ja lopun historian kanssa.",
    notes: "Muistiinpanot",
    editNotes: "Muokkaa muistiinpanoja",
    notesLabel: "{{name}} — muistiinpanot",
    noNotes:
      "Ei vielä muistiinpanoja. Osoitteet, kytkennät, mitä salasanaa se haluaa, mikä siinä hajoaa.",
    tablistLabel: "mitä tästä penkistä näytetään",
    tabRuns: "Ajot",
    tabSetup: "Asetukset",
    tabAccess: "Pääsy",
    tabNotes: "Muistiinpanot",
    tabTimeline: "Aikajana",
    heldBy: "Pitäjä: {{actor}}",
    minutesLeft: "{{count}} min jäljellä",
    pastDeadline: "yli määräajan",
    quietFor: " · hiljaa {{count}} min",
    release: "Vapauta…",
    taken: "otettu {{when}}",
    confirmUse: "<b>{{actor}}</b> käyttää tätä penkkiä. Sen vapauttaminen nyt voi rikkoa käynnissä olevan ajon, ja haltuunotto kirjataan sinun nimiisi.",
    confirmUseFor: "<b>{{actor}}</b> käyttää tätä penkkiä: {{purpose}}. Sen vapauttaminen nyt voi rikkoa käynnissä olevan ajon, ja haltuunotto kirjataan sinun nimiisi.",
    leaveIt: "Anna olla",
    takeIt: "Ota penkki",
    whoHasHadIt: "kenellä se on ollut ({{count}})",
    tookFrom: " (keneltä: {{actor}})",
  },

  quality: {
    answerAllFresh: "{{count}} ryhmää arvioitu, kaikki tuoreita.",
    title: "Laatu",
    heading: "Laatu",
    intro:
      "Arvioitu näkemys, ei läpimenoprosentti.",
    empty: "Arviota ei ole vielä julkaistu. Penkki lähettää sen seuraavan ajon mukana.",
    whichBench: "mikä penkki",
    noReview: " (ei arviota)",
    fromBench: "lähde: {{bench}}",
    received: "vastaanotettu {{when}}",
    staleCount: "{{count}} / {{total}} arviosta on vanhentunut",
    chipWeak: "C tai huonompi ({{count}})",
    chipStale: "Vanhentuneet ({{count}})",
    colGrade: "Arvosana",
    colGroup: "Ryhmä",
    colScenario: "Skenaariolla",
    colEverPassed: "Mennyt läpi täällä",
    colConfidence: "Luottamus",
    staleBadge: "vanhentunut",
    staleTitle: "skenaariot ovat muuttuneet edellisen katselmoinnin jälkeen",
    everPassedTitle: "vastattu kaikista ajoista jotka tämä palvelu on saanut",
    neverSuffix: " · {{count}} ei koskaan",
    gaps: "Aukot",
    recommendations: "Suositukset",
    neverPassed: "Ei koskaan nähty menevän läpi täällä:",
    reviewed: "katselmoitu {{when}}",
    reviewedBy: ", tekijä {{who}}",
    gloss: {
      A: "todistaa mitä väittää",
      B: "pitää kutinsa",
      C: "ajautuu, mutta todistaa vähän",
      D: "todiste on vain näennäinen",
      E: "ei todistetta lainkaan",
    },
  },

  runs: {
    answerLoaded_one: "{{count}} ajo ladattu.",
    answerLoaded_other: "{{count}} ajoa ladattu.",
    answerFailures_one: "Niistä {{count}} sisältää epäonnistumisia.",
    answerFailures_other: "Niistä {{count}} sisältää epäonnistumisia.",
    answerNoFailures: "Yksikään niistä ei epäonnistunut.",
    title: "Ajot",
    heading: "Ajot",
    searchPlaceholder: "hae ajoa nimellä",
    searchLabel: "hae ajoja nimellä",
    allBenches: "Kaikki penkit",
    allProducts: "Kaikki tuotteet",
    hasFailures: "Sisältää epäonnistumisia",
    clean: "Puhtaat",
    wholeSuite: "Koko sarja",
    slices: "Osajoukot",
    empty:
      "Ei vielä raportoitua. Ajo ilmestyy tähän heti kun sen agentti avaa sen.",
    colRun: "Ajo",
    colBench: "Penkki",
    colBuild: "Koonti",
    colStarted: "Alkoi",
    colTook: "Kesto",
    colSteps: "Askelia",
    colResult: "Tulos",
    loadedFiltered: "{{shown}} / {{total}} ladatusta",
    loaded: "{{count}} ladattu",
    moreOnServer: ", palvelimella on lisää",
    thatIsAll: ", siinä kaikki",
    clearFilters: "tyhjennä suodattimet",
    loadMore: "Lataa {{count}} lisää",
    filteringNote_one:
      "Suodatetaan tähän mennessä ladattua {{count}} ajoa, ei koko historiaa.",
    filteringNote_other:
      "Suodatetaan tähän mennessä ladattuja {{count}} ajoa, ei koko historiaa.",
    today: "Tänään",
    yesterday: "Eilen",
    dayLabel: "{{weekday}} {{day}}. {{month}}",
    dayLabelWithYear: "{{weekday}} {{day}}. {{month}} {{year}}",
  },

  manual: {
    answerCases_one: "{{count}} tapaus tehdään käsin.",
    answerCases_other: "{{count}} tapausta tehdään käsin.",
    title: "Manuaalityö",
    heading: "Manuaalityö",
    empty:
      "Mitään ei ole vielä julkaistu. Tämä dokumentti lasketaan penkissä ajon alussa — se ilmestyy siis seuraavan ajon jälkeen penkissä, jonka harness vie sen ulos, eikä koskaan sellaisessa, jonka harness on sitä vanhempi.",
    benchOn: "penkissä {{bench}}",
    published: "julkaistu {{when}}",
    notKnown: "penkki ei tiedossa",
    minutes: "{{count}} min",
    shed_one:
      "{{count}} tapaus pudotettiin mahtumisen vuoksi — dokumentti oli liian suuri tallennettavaksi kokonaan.",
    shed_other:
      "{{count}} tapausta pudotettiin mahtumisen vuoksi — dokumentti oli liian suuri tallennettavaksi kokonaan.",
    jiraAbsent:
      "Penkissä ei ole Jira-dataa, joten yksikään alla oleva tapaus ei kanna kattavuustuomiota. Menettelyt ovat silti oikein — ne tulevat suunnitelmasta.",
    jiraStale:
      "Jira-tilannekuva on {{days}} päivää vanha (haettu {{when}}). Kaikki XSNCF:ssä sen jälkeen muuttunut on tässä näkymätöntä.",
    capsUnknown:
      "Penkin ominaisuuksia ei tiedetty kun tämä laskettiin, joten mitään ei ole merkitty täällä ohitettavaksi — ei siksi ettei mikään ohittuisi.",
    metaPriority: "prioriteetti {{priority}}",
    metaLevel: "vaatii {{level}}-tason pääsyn",
    metaMinutes: "~{{count}} min",
    metaNeeds: "vaatii: {{caps}}",
    before: "Ennen aloitusta:",
    procedure: "Menettely — {{total}} askelta, {{manual}} käsin",
    furtherSteps: "{{count}} lisäaskelta ei näytetä.",
    passWhen: "Menee läpi kun:",
    jira: "Jira",
    closed: " (suljettu)",
    colField: "Kenttä",
    colJira: "Jira sanoo",
    colPlan: "Suunnitelma sanoo",
    wordingDiffers:
      "sanamuoto eroaa ({{count}}) — lue, älä välttämättä muuta",
    issueProblems: "Ongelmat tiketeissä",
    action: "Toimenpide:",
    verdict: {
      synced: "synkassa",
      drifted: "eriytynyt",
      unknown: "tuntematon",
      missing: "puuttuu",
    },
  },

  launch: {
    tagRig: "skriptit",
    tagTests: "testit",
    tagAlias: "buildin toinen nimi",
    title: "Käännä ensin uusi versio",
    noTarget:
      "Tälle projektille ei ole määritetty käynnistyskohdetta, joten täältä ei voi vielä käynnistää mitään.",
    noToken:
      "Tällä palvelimella ei ole käynnistämiseen tarvittavaa tunnistetta: <code>{{env}}</code> puuttuu. Operaattori asettaa sen isäntäkoneella; tunnistetta ei koskaan säilytetä täällä.",
    target: "Pyytää <code>{{project}}</code> haarassa <code>{{ref}}</code> kääntämään ja ajamaan.",
    onRig: "jo tällä penkillä — sekunteja",
    readyNow: "valmiina heti",
    needsBuild: "vaatii käännöksen (~15 min)",
    typeIt: "— kirjoita arvo —",
    partialList: "Kaikkia arvoja ei saatu listattua: {{why}}. Voit silti kirjoittaa arvon.",
    emptyList: "Valittavaa ei vielä ole. Kirjoita arvo itse.",
    noList: "Listaa ei ole: {{why}}. Kirjoita arvo itse.",
    whichRig: "Putki päättää, mille penkille se menee; tämä lomake ei valitse sitä.",
    reason: "Miksi — seuraava lukee tämän, kuten varauksen syyn",
    start: "Käännä ja aja",
    starting: "Pyydetään…",
    cost: "Kääntää version ja ajaa sen penkillä — tämä päätyy laitteiston liikkeeseen.",
    needOperator: "Käännöksen voi käynnistää operaattori tai ylempi.",
    needFields: "Puuttuu vielä: {{fields}}",
    recorded: "Yritys tallennettiin numerolla #{{id}}, alla.",
    opened: "Putki #{{id}} avattu",
    history_one: "{{count}} aiempi käynnistys",
    history_other: "{{count}} aiempaa käynnistystä",
  },
  testSelections: {
    title: "Tarjotut testit",
    usingDefaults: "palvelun oletukset",
    count_one: "{{count}} projektin asettama valinta",
    count_other: "{{count}} projektin asettamaa valintaa",
    what: "Ajolomakkeen Testit-lista tämän projektin jokaisella penkillä. Merkki annetaan pytestille; nimen ihmiset näkevät.",
    marker: "pytest-merkki",
    label: "Nimi",
    labelPlaceholder: "mitä ihmiset näkevät",
    add: "Lisää valinta",
    save: "Tallenna lista",
    saving: "Tallennetaan…",
    useDefaults: "Palaa palvelun oletuksiin",
    blank: "Jokainen valinta tarvitsee merkin ja nimen.",
    needMaintainer: "Tätä listaa voi muuttaa ylläpitäjä tai ylempi.",
    up: "ylös",
    down: "alas",
    remove: "poista",
  },
  reports: {
    answerSaved_one: "{{count}} raportti tallennettu.",
    answerSaved_other: "{{count}} raporttia tallennettu.",
    title: "Raportit",
    heading: "Raportit",
    newReport: "Uusi raportti",
    intro:
      "Rajaus ja järjestetty lista osioita, tallennettuna ja jaettuna. Tarkkuus valitaan osiokohtaisesti, joten ”epäonnistumiset kokonaan, loput listana” on raportti jonka voi säilyttää.",
    sectionCount_one: "{{count}} osio",
    sectionCount_other: "{{count}} osiota",
    edited: " · muokattu {{when}}",
    editedBy: ", tekijä {{who}}",
    delete: "poista",
    empty: "Ei vielä tallennettuja raportteja.",
    lookLegend: "Ulkoasu ja brändi",
    lookNote:
      "Käytetään jokaiseen renderöityyn raporttiin, sekä HTML- että PDF-muodossa. Tulosten värit tulevat oletuksena penkkinäkymän paletista, joka on tarkistettu värinäön erottuvuuden osalta — ohita ne tietoisesti.",
    brand: "Brändi",
    accent: "Korostusväri",
    accentLabel: "korostusväri",
    strapline: "Iskulause (otsikkopalkki ja jokaisen PDF:n alatunniste)",
    verdictColours: "Tulosten värit",
    verdictFg: "tekstin väri: {{verdict}}",
    verdictBg: "taustaväri: {{verdict}}",
    saveLook: "Tallenna ulkoasu",
    resetDefaults: "Palauta oletukset",
    name: "Nimi",
    whatFor: "Mihin se on",
    whatForHint: "— ajosivu näyttää tämän osoittimen alla",
    scope: "Rajaus",
    campaign: "Kampanja",
    campaignNone: "— ei mitään: viimeiset N ajoa —",
    campaignOption: "#{{id}} · {{build}} penkillä {{bench}} · {{status}}",
    campaignScope: "Kampanjan ajot",
    campaignDrop: "pudota kampanja",
    campaignStatus: {
      running: "yhä käynnissä",
      green: "kaikki ajettiin, mikään ei epäonnistunut",
      not_green: "ei kaikki vihreää",
      cancelled: "peruttu",
    },
    lastRuns: "Viimeiset N ajoa",
    benches: "Penkit",
    any: "mikä tahansa",
    from: "Alkaen",
    to: "Asti",
    casesMatching: "Osuvat tapaukset (glob, esim. CERT-*)",
    verdicts: "Tuomiot",
    verdictsHint:
      "— jos mitään ei ole rastitettu, mukaan tulevat kaikki tuomiot. Osan rastittaminen tarkoittaa VAIN niitä, ja se jää helposti puolitiehen: raportti nimeltä ”täysi”, jossa on rastitettu vain <code>passed</code>, ei sisällä yhtään epäonnistumista.",
    sectionsLegend: "Osiot järjestyksessä",
    sectionType: "osion tyyppi",
    detail: "tarkkuus",
    layout: "asettelu",
    moveUp: "siirrä ylös",
    moveDown: "siirrä alas",
    removeSection: "poista osio",
    notesText: "muistiinpanon teksti",
    addSection: "Lisää osio",
    preview: "Esikatselu",
    previewTitle: "raportin esikatselu",
    previewPrompt: "Paina Esikatselu renderöidäksesi tämän määrittelyn.",
    deleteTitle: "Poistetaanko {{name}}?",
    deleteWarn:
      "Kaikki menettävät tämän määrittelyn, ja jokaiselta ajosivulta katoaa painike joka renderöi sen. Jo ladatut dokumentit säilyvät — tämä poistaa reseptin, ei sitä mitä se tuotti.",
    keepIt: "Säilytä",
  },

  runDetail: {
    casePage: "Tapauksen sivu",
    took: "kesti {{duration}}",
    answerRunning: "Ajo on vielä käynnissä.",
    answerAwaiting: "Valmis; tulokset ovat vielä tulossa.",
    answerLost: "Valmis, mutta tulokset eivät koskaan saapuneet.",
    notComparedNoProfile: "Tuloksia ei verrata aiempaan ajoon: tämä ajo ei kirjaa, millä profiililla se ajettiin.",
    notComparedNoData: "Tuloksia ei verrata aiempaan ajoon: tämä ajo ei kirjaa, mitä testidataa se käytti.",
    notComparedData: "Tuloksia ei verrata ajoon <runLink>{{run}}</runLink>, tämän profiilin edelliseen ajoon: siinä oli eri testidata, joten muuttunut tulos voisi johtua datasta eikä tuotteesta.",
    askedBy: "pyytäjä: {{who}}",
    partOfCampaign: "osa kampanjaa",
    movedWhatTests: "testit",
    movedSentenceProfile: "Tämän profiilin edellisen ajon <runLink>{{run}}</runLink> jälkeen muuttui: {{what}}.",
    testData: "testidata {{digest}}",
    testDataTitle: "Profiilin testidatan sormenjälki: tallenteet, referenssikuvat, asettelupaketti",
    answerNothingFailed: "Mikään ei epäonnistunut.",
    answerAllKnown_one: "{{count}} epäonnistuminen, tunnettu ongelma.",
    answerAllKnown_other: "Kaikki {{count}} epäonnistumista ovat tunnettuja ongelmia.",
    answerFailed_one: "{{count}} testi epäonnistui.",
    answerFailed_other: "{{count}} testiä epäonnistui.",
    answerPassed_one: "{{count}} läpi",
    answerPassed_other: "{{count}} läpi",
    answerExpected_one: "{{count}} odotettu epäonnistuminen",
    answerExpected_other: "{{count}} odotettua epäonnistumista",
    answerNotRun_one: "{{count}} ei ajettu täällä",
    answerNotRun_other: "{{count}} ei ajettu täällä",
    buildNamed: "koonti {{build}}",
    details: "Lisätiedot: koonti, testidata, itsetestit",
    movedWhatBuild: "koonti",
    movedWhatHarness: "testikehys",
    movedWhatData: "testidata",
    movedSentence: "Ajon <runLink>{{run}}</runLink> jälkeen muuttui: {{what}}.",
    back: "← ajot",
    fallbackTitle: "Ajo {{id}}",
    pageTitle: "{{run}} penkissä {{bench}}",
    onBench: "penkissä {{bench}}",
    finished: "valmis",
    running: "käynnissä",
    deleteThisRun: "poista tämä ajo",
    sameVariantBuild: "Sama {{variant}} toisella koonnilla ({{from}} → {{to}}), verrattu ajoon",
    variantFallback: "variantti",
    tileScenarios: "Kaikki tapaukset",
    tileFailed: "Epäonnistui",
    tileXfailed: "Odotetut epäonnistumiset",
    tilePassed: "Läpi",
    tileSkipped: "Ei ajettu täällä",
    tileSteps: "Askelia",
    tileShowAll: "näytä taas kaikki",
    tileShowOnly: "näytä vain: {{label}}",
    filterPlaceholder: "suodata nimellä",
    filterLabel: "suodata skenaarioita nimellä",
    showing: "Näytetään {{shown}} / {{total}}.",
    showEverything: "näytä kaikki",
    awaitingVerdicts:
      "Penkki ei ole vielä lähettänyt tämän ajon tuloksia, joten nämä ovat epäonnistuneita <b>tarkistuksia</b>, eivät testien tuloksia. Luonnostesti, joka ei läpäise tarkistusta, on odotettu epäonnistuminen, ei regressio.",
    lostVerdicts:
      "Penkki ei koskaan lähettänyt tämän ajon tuloksia, joten nämä ovat epäonnistuneita <b>tarkistuksia</b>, eivät testien tuloksia. Ajo pysähtyi todennäköisesti ennen kuin testit ehtivät raportoida; loki kertoo missä.",
    noFailuresMatch: "Mikään epäonnistunut ei osu suodattimeen.",
    nothingFailed: "Tässä ajossa ei epäonnistunut mikään.",
    sectionFailed: "Epäonnistuneet",
    sectionFailedN: "Epäonnistuneet ({{count}})",
    sectionExpected: "Odotetut epäonnistumiset — luonnokset ({{count}})",
    sectionPassed: "Läpi menneet ({{count}})",
    sectionSkipped: "Ohitetut ({{count}})",
    nothingMatches: "Mikään ei osu. Yllä olevat luvut koskevat koko ajoa.",
    noCases: "Tälle ajolle ei kirjattu yhtään tapausta.",
    tablistLabel: "mitä tästä ajosta näytetään",
    tabFailures: "Epäonnistumiset",
    tabAll: "Kaikki tapaukset",
    tabReport: "Raportti",
    tabLog: "Loki",
    deleteTitle: "Poista {{name}}",
    deleteWarn:
      "Tämä poistaa ajon ja kaiken siihen kirjatun — askeleet, tuomiot, kuvat ja rakennetut raportit. Tätä ei voi perua. Kirjoita <name>{{name}}</name> vahvistaaksesi.",
    buildChangedSince: "koonti muuttui ajon <runLink>{{run}}</runLink> jälkeen",
    harnessDigestTitle: "tiiviste jokaisesta harness-tiedostosta",
    harness: "harness {{digest}}",
    productDataTitle:
      "tiiviste tuotteen skenaarioista, suunnitelmasta, referensseistä ja sidecar-tiedostoista",
    productData: "{{product}}-data {{digest}}",
    productFallback: "tuote",
    movedHarness: "harness",
    movedProductData: "tuotedata",
    movedJoin: " ja ",
    movedSince: "{{what}} muuttui ajon <runLink>{{run}}</runLink> jälkeen",
    none: "Ei mitään.",
    groupTests_one: "{{count}} testi",
    groupTests_other: "{{count}} testiä",
    tallySkipped_one: "{{count}} ei ajettu täällä",
    tallySkipped_other: "{{count}} ei ajettu täällä",
    caseSteps_one: "{{count}} askel",
    caseSteps_other: "{{count}} askelta",
    caseFailedChecks_one: " · {{count}} epäonnistunut tarkistus",
    caseFailedChecks_other: " · {{count}} epäonnistunutta tarkistusta",
    shouldShow: "Pitäisi näyttää",
    definition: "määrittely",
    noSteps: "Tämä tapaus ei kirjannut yhtään vaihetta; yllä oleva tulos on kaikki, mitä siitä jäi.",
    browseCase: "Tämä tapaus projektin muissa ajoissa",
    requires: "Vaatii",
    preconditions: "Esiehdot",
    expected: "Odotettu",
    returned: "Palautti",
    stdout: "stdout",
    stderr: "stderr",
    sent: "lähetetty",
    truncated: "… katkaistu",
    goldenRecordedFrom: "Referenssi tallennettu lähteestä",
    goldenElsewhere: "Referenssi tallennettu muualla: {{list}}.",
    goldenMissed: "Näyttö poikkeaa referenssistä: samankaltaisuus {{score}}, kynnys {{threshold}} (puuttuu {{gap}}).",
    goldenMaskedMuch: "{{pct}} % ruudusta on peitetty eikä sitä verrattu.",
    stepGiven: "Mitä askel sai",
    stepRaised: "Askel {{step}} · {{action}} keskeytyi virheeseen:",
    goldenElsewhereDisplayOs: "DisplayOS silloin {{golden}}, nyt {{current}}",
    goldenElsewhereLayout: "asettelu silloin {{golden}}, nyt {{current}}",
    goldenElsewhereLayoutFile: "toinen asettelutiedosto",
    goldenElsewhereFonts: "eri fontit",
    goldenElsewhereCapture: "kaappaus silloin {{golden}}, nyt {{current}}",
    goldenElsewhereOther: "{{key}} silloin {{golden}}, nyt {{current}}",
    goldenElsewhereUnknown: "Ei voitu todeta, tallennettiinko referenssi täällä: {{detail}}",
    theMiss: "ero jonka tämä askel väittää",
    recorded: "tallennettu",
    thresholdSource: "kynnys: {{source}}",
    masked: "maskattu {{list}}",
    maskedFraction: " · {{pct}} % kuvasta",
    stillScreenFloor: "paikallaan olevan ruudun pohja {{score}}",
    belowNoise:
      "Kynnys on tiukempi kuin ero, jonka kaksi kaappausta samasta ruudusta antoivat referenssiä tallennettaessa. Tämä vertailu ei voi mennä läpi luotettavasti, teki tuote mitä tahansa: maskaa liikkuva osa tai löysää kynnystä.",
    colRegion: "alue",
    colScore: "pisteet",
    colThreshold: "kynnys",
    notScored: "ei pisteytetä kuvassa ”{{label}}”: [{{rect}}]",
    frameFallback: "kuva",
    blink: "vilkuta",
    blinkTitle: "vaihtele kuvan ja referenssin välillä",
    mask: "maski",
    maskTitle: "piirrä pisteytyksen ulkopuolelle jätetyt alueet",
    blinking: " · vilkkuu",
    maskedAreas_one: " · {{count}} maskattu alue, ei pisteytetä",
    maskedAreas_other: " · {{count}} maskattua aluetta, ei pisteytetä",
    frameAlt: "{{label}} askeleesta {{step}}",
    frameLabel: { frame: "kuva", golden: "referenssi", difference: "erotus" },
    scene: {
      verdict_one: "{{count}} ero {{compared}} elementissä",
      verdict_other: "{{count}} eroa {{compared}} elementissä",
      elements_one: "{{count}} elementti",
      elements_other: "{{count}} elementtiä",
      nothingCompared: "mitään ei verrattu",
      within: "rajattu: {{path}}",
      ignored: "ohitettu {{list}}",
      ignoredFields: "ohitetut kentät: {{list}}",
      settled_one: "vakiintui {{count}} kaappauksen jälkeen",
      settled_other: "vakiintui {{count}} kaappauksen jälkeen",
      colPath: "polku",
      colField: "kenttä",
      colGolden: "hyväksytty",
      colNow: "nyt",
      absent: "puuttuu",
      showAll_one: "näytä kaikki ({{count}})",
      showAll_other: "näytä kaikki ({{count}})",
      showFewer: "näytä vähemmän",
      added: "Ilmestyi",
      removed: "Katosi",
      notListed_one: "{{count}} ero lisää ei mahdu tähän; koko luettelo on ajon vieressä: {{detail}}",
      notListed_other: "{{count}} eroa lisää ei mahdu tähän; koko luettelo on ajon vieressä: {{detail}}",
    },
    nowRunning: "nyt käynnissä",
    selfTestPassed: "Harnessin itsetestit menivät läpi ennen tätä ajoa.",
    selfTestPassedWith:
      "Harnessin itsetestit menivät läpi ennen tätä ajoa — {{summary}}.",
    selfTestFailed:
      "<b>Harness ei läpäissyt omia testejään.</b> Mitä tahansa tämä ajo tuotteesta sanookin, sen mittasi harness joka ei pysy kasassa — lue alla olevat luvut se mielessä, tai älä lainkaan.",
    logSize: "{{kb}} kB, sellaisena kuin ajo sen kirjoitti.",
    runLog: "Ajon loki ({{kb}} kB)",
    since: "Verrattu ajoon",
    compared: " (vertailtu {{count}})",
    nothingChanged: "Yhdenkään testin tulos ei muuttunut.",
    onlyThen_one:
      " {{count}} skenaario siitä ajosta ei ajautunut tässä.",
    onlyThen_other:
      " {{count}} skenaariota siitä ajosta ei ajautunut tässä.",
    newlyFailing: "uusia epäonnistumisia ({{count}})",
    newlyPassing: "uusia läpimenoja ({{count}})",
    reportHeading: "Raportti",
    noTemplates: "Raporttipohjia ei ole määritelty. <link>Lisää yksi</link>.",
    reportNote:
      "Palvelu renderöi tämän ajon omista tiedoista — se toimii vaikka penkki olisi varattu, poissa verkosta tai kokonaan poissa. Sisältöä muokataan kohdassa <link>Raportit</link>.",
    asPdf: "{{name}} PDF-muodossa",
    archive_one: "Arkisto: {{count}} PDF rakennettu penkissä ajon aikana",
    archive_other: "Arkisto: {{count}} PDF:ää rakennettu penkissä ajon aikana",
    pdfHelp: {
      summary: "vain tuomiot ja yhteenveto — ei tapauskohtaisia tietoja",
      results: "jokainen tapaus tuomioineen ja perusteluineen — ei askel askeleelta",
      failures: "epäonnistuneet tapaukset kokonaan, ei muuta",
      standard: "jokainen tapaus askel askeleelta, ilman kuvakaappauksia",
      full: "jokainen tapaus penkin ottamien kuvakaappausten kanssa",
    },
    phaseCleanup: "Siivous",
    phaseSetup: "Ennen ensimmäistä askelta",
    phasePassed: "läpi",
    phasePassedRestored: "läpi — {{count}} palautettu",
    phaseFailed: "epäonnistui",
    saved: "Tallennettu",
    runningEllipsis: "käynnissä…",
    noAssertion: "ei väitettä — vain valmistelu",
    fail: "FAIL",
    pass: "PASS",
    mb: "{{n}} MB",
    kb: "{{n}} kB",
  },
  human: {
    step: "Vaihe {{n}}",
    timeout_one: "{{secs}} s ja {{count}} yrityksen jälkeen:",
    timeout_other: "{{secs}} s ja {{count}} yrityksen jälkeen:",
    rc: "Paluukoodi {{rc}} eikä {{want}}",
    rcNested: "paluukoodi {{rc}} eikä {{want}}",
    noMessage: "ei virheilmoitusta",
    expected: "odotettiin",
    unchanged: "ei muuttunut, vaikka sen piti",
    notContains: "ei sisällä",
    noMatch: "ei vastaa",
    outOfRange: "välin {{lo}}–{{hi}} ulkopuolella",
    missing: "puuttuu vastauksesta",
    stdoutLacks: "Tuloste ei sisällä",
    stdoutHas: "Ei-toivottua tulosteessa:",
    stdoutNe: "Tuloste on",
    stdoutSame: "Tuloste ei muuttunut:",
    httpTimeout: "Ei HTTP-vastausta osoitteesta {{host}}:{{port}} {{secs}} sekunnissa",
    httpDropped: "Yhteys katkesi ilman vastausta",
    notJson: "Vastaus ei ollut JSONia",
    cmdTimeout: "Komento ei päättynyt {{secs}} sekunnissa",
    cmdFailed: "Komento päättyi koodiin {{rc}}",
    truncated: "(harness katkaisi loput)",
    raw: "Alkuperäinen viesti",
    moreLines_one: "(+{{count}} rivi)",
    moreLines_other: "(+{{count}} riviä)",
    until: "kunnes {{what}}",
    and: "ja",
    within: "enintään {{secs}} s",
    every: "{{secs}} s välein",
    seconds: "{{v}} s",
    output: "tuloste",
    a: {
      rc: "koodi {{v}}",
      status: "HTTP {{v}}",
      truthy: "{{p}} asetettu",
      falsy: "{{p}} tyhjä",
      exists: "{{p}} löytyy",
      absent: "ei {{p}}",
      contains: "{{p}} sisältää {{v}}",
      stdoutContains: "tuloste sisältää {{v}}",
      stdoutLacks: "tuloste ei sisällä {{v}}",
      stdoutNotBlank: "tuloste ei tyhjä",
      notBlank: "kuva ei tyhjä",
      frameDiffers: "kuva eri kuin {{v}}",
      size: "kuva {{w}}×{{h}}",
    },
  },
};

export default fi;
